import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Copy as CopyIcon, Dna } from "lucide-react";
import api, { notifyError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const CATEGORIES = ["Retail & Ecommerce", "Fashion", "Beauty", "Food & Beverage",
  "Automotive", "Home & Living", "Electronics", "Sports", "Vape"];

const slugify = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60);

const inputCls = "w-full rounded-lg bg-black/30 border border-white/10 text-white px-3 py-2 text-sm focus:border-cyan-500/50 outline-none";
const labelCls = "text-[10px] uppercase tracking-widest text-white/50 font-black mb-1 block";

export const TemplateCloneWizard = ({ open, onOpenChange, templates = [], defaultSourceId = "" }) => {
  const navigate = useNavigate();
  const [presets, setPresets] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    source_template_id: "", name: "", slug: "", description: "",
    category: CATEGORIES[0], thumbnail: "", dna_preset: "",
  });
  const [slugTouched, setSlugTouched] = useState(false);

  useEffect(() => {
    if (open) {
      setForm((f) => ({ ...f, source_template_id: defaultSourceId || templates[0]?.id || "" }));
      setSlugTouched(false);
      api.get("/platform/template-registry/dna-presets")
        .then((r) => setPresets(r.data.items || []))
        .catch(() => setPresets([]));
    }
  }, [open, defaultSourceId, templates]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onNameChange = (v) => {
    setForm((f) => ({ ...f, name: v, slug: slugTouched ? f.slug : slugify(v) }));
  };

  const submit = async () => {
    if (!form.source_template_id) return toast.error("Pilih source template.");
    if (!form.name.trim()) return toast.error("Nama template wajib diisi.");
    if (!form.slug.trim()) return toast.error("Slug wajib diisi.");
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.dna_preset) delete payload.dna_preset;
      if (!payload.thumbnail) delete payload.thumbnail;
      const r = await api.post("/platform/template-registry/clone", payload);
      toast.success(`Template "${r.data.name}" berhasil di-clone (DRAFT).`);
      onOpenChange(false);
      navigate(`/platform/template-registry/${r.data.id}/designer`);
    } catch (e) {
      notifyError(e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121D33] border-white/10 text-white max-w-lg" data-testid="clone-wizard">
        <DialogHeader>
          <DialogTitle className="font-display font-black flex items-center gap-2">
            <CopyIcon size={16} className="text-cyan-400" /> Clone Template
          </DialogTitle>
          <DialogDescription className="text-white/50 text-xs">
            Layout, theme, branding, configuration, SEO, feature mapping & DNA ikut ter-clone.
            Demo content dikosongkan — isi via Designer. Data tenant TIDAK PERNAH ikut.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <label className={labelCls}>Source Template</label>
            <select value={form.source_template_id} onChange={(e) => set("source_template_id", e.target.value)} className={inputCls} data-testid="clone-source">
              {templates.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.slug})</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Template Name</label>
              <input value={form.name} onChange={(e) => onNameChange(e.target.value)} className={inputCls} placeholder="Fashion Store" data-testid="clone-name" />
            </div>
            <div>
              <label className={labelCls}>Slug</label>
              <input value={form.slug} onChange={(e) => { setSlugTouched(true); set("slug", slugify(e.target.value)); }} className={`${inputCls} font-mono`} placeholder="fashion-store" data-testid="clone-slug" />
            </div>
          </div>
          <div>
            <label className={labelCls}>Description</label>
            <textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={2} className={inputCls} placeholder="Deskripsi template…" data-testid="clone-description" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Category</label>
              <select value={form.category} onChange={(e) => set("category", e.target.value)} className={inputCls} data-testid="clone-category">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Thumbnail URL (opsional)</label>
              <input value={form.thumbnail} onChange={(e) => set("thumbnail", e.target.value)} className={inputCls} placeholder="https://…" data-testid="clone-thumbnail" />
            </div>
          </div>
          <div>
            <label className={labelCls}><Dna size={10} className="inline mr-1" />DNA Preset (opsional)</label>
            <select value={form.dna_preset} onChange={(e) => set("dna_preset", e.target.value)} className={inputCls} data-testid="clone-dna-preset">
              <option value="">— Pertahankan DNA source —</option>
              {presets.map((p) => <option key={p.key} value={p.key}>{p.label} · {p.dna.inventory_units.join("/")}</option>)}
            </select>
            {form.dna_preset && (
              <div className="mt-2 rounded-lg border border-cyan-500/30 bg-cyan-500/10 p-2 text-[10px] text-cyan-200" data-testid="clone-preset-summary">
                Homepage: {presets.find((p) => p.key === form.dna_preset)?.dna.homepage_sections.join(" · ")}
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-white/70 hover:text-white hover:bg-white/10" data-testid="clone-cancel">Batal</Button>
          <Button onClick={submit} disabled={saving} className="bg-gradient-to-r from-cyan-500 to-purple-500 text-white font-black" data-testid="clone-submit">
            {saving ? "Cloning…" : "Clone Template"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TemplateCloneWizard;
